import React, {PropsWithChildren, useMemo} from 'react'
import {FormattedMessage} from 'react-intl'
import {HStack, Text, useMultiStyleConfig, VStack, Box, Link} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Button, Icon, ApplePayWrapper, PayPalButtonWrapper} from '_uiComponents'
import {useCurrentBasket, useAnalytics} from '_customHooks'
import {MiniCartPreferences} from '_types'
import {useGlobalConfigState} from '_context'

interface MiniCartProps {
    minicartPreferences: MiniCartPreferences
    footerFixed: boolean
}

const MinicartFooter = ({minicartPreferences, footerFixed = true}: MiniCartProps) => {
    const variant = !footerFixed ? 'relativeFooter' : undefined
    const styles = useMultiStyleConfig('MinicartFooter', {variant}) as Record<string, PropsWithChildren>
    const {locale} = useMultiSite()
    const {appPreferences} = useGlobalConfigState()
    const {CountriesWithZeroPrices} = appPreferences
    const aliasCountry = useMemo(() => locale.alias?.split('-')[1].toLowerCase(), [locale])

    const hasHiddenPrices = useMemo(
        () => (Array.isArray(CountriesWithZeroPrices) ? CountriesWithZeroPrices.find((country: string) => country.toLowerCase() === aliasCountry) : undefined),
        [CountriesWithZeroPrices, aliasCountry],
    )
    // Read-only consumer: light useCurrentBasket (same query, no mutation hooks/effects).
    const {data: basket} = useCurrentBasket()
    const basketCurrency = basket?.currency
    const [localCurrency] = React.useState(basketCurrency)

    const {analyticsGoToCart} = useAnalytics()

    const {PAYPAL_EXPRESS_Allowed_Currencies, PP_API_Button_Location, paypal_clientId, paypal_button_style, ApplePay_Enabled} = minicartPreferences || {}

    const showPaypalButton =
        localCurrency &&
        PAYPAL_EXPRESS_Allowed_Currencies?.includes(localCurrency) &&
        PP_API_Button_Location?.[0]?.includes('Minicart') &&
        paypal_clientId &&
        paypal_button_style

    const showApplePayButton = ApplePay_Enabled && typeof window !== 'undefined' && window?.ApplePaySession?.canMakePayments()

    const allOrderable = basket?.c_validProducts

    return (
        <VStack data-testid="minicart-footer" {...styles.wrapperfooter}>
            <HStack {...styles.subtotalSection}>
                <FormattedMessage id="label.subtotal" defaultMessage="SUBTOTAL" />
                <Text as="span" data-testid="minicart-footer-subtotal">
                    {basket?.c_subtotal?.formattedValue}
                </Text>
            </HStack>

            {!hasHiddenPrices && (
                <>
                    {/* CTA rendered as an anchor (Button as={Link} → <a href>) so it keeps the link role:
                        this navigates to the cart, so semantically it's a link, not a button (a11y:
                        Structure & Semantics — use <a href> to navigate; 4.1.2 role, 2.4.4 link purpose).
                        `type="button"` removes the custom Button's default type="submit" (inert on an
                        anchor, but avoids any form-submit ambiguity). Navigation stays native via href, so
                        it adapts if c_goToCartUrl migrates from SFRA to a PWA route. */}
                    <Button as={Link} type="button" href={basket?.c_goToCartUrl} onClick={analyticsGoToCart} variant="primary" {...styles.cartButton}>
                        <FormattedMessage id="button.cart" defaultMessage="GO TO THE CART" />
                        <Icon name="IconCart" size="sm" />
                    </Button>

                    {(showApplePayButton || showPaypalButton) && allOrderable && (
                        <VStack {...styles.expressSection}>
                            <Text as="span" {...styles.expressLabel}>
                                <FormattedMessage id="label.cart.otherwise" defaultMessage="or proceed with quick payment" />
                            </Text>

                            <ApplePayWrapper
                                customPrefs={minicartPreferences}
                                basketId={basket?.basketId}
                                currency={basketCurrency}
                                subTotal={basket?.c_subtotal?.value}
                                allOrderable={allOrderable}
                                buttonLocation="minicart"
                            />

                            <Box {...styles.expressPaypalBox}>
                                <PayPalButtonWrapper
                                    sitePreference={minicartPreferences}
                                    basketId={basket?.basketId}
                                    currency={basketCurrency}
                                    subTotal={basket?.c_subtotal?.value}
                                    allOrderable={allOrderable}
                                    buttonLocation="minicart"
                                />
                            </Box>

                            <Text as="span" {...styles.notice}>
                                <FormattedMessage id="msg.present.checkout" defaultMessage="The gift option is not available with Express Checkout" />
                            </Text>
                        </VStack>
                    )}
                </>
            )}
        </VStack>
    )
}

export {MinicartFooter}
